import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormSection } from '../ui/FormSection';

export type StagedUploadStatus = 'pending' | 'uploading' | 'completed' | 'error';

export interface StagedFile {
  id: string;
  file: File;
  previewUrl: string;
  status: StagedUploadStatus;
  errorMessage?: string;
}

export interface StagedImageUploaderProps {
  mode: 'single' | 'multi';
  value: StagedFile[];
  onChange: (files: StagedFile[]) => void;
  label: string;
  helperText?: string;
  maxFiles?: number;
  maxSizeBytes?: number;
  accept?: string;
  disabled?: boolean;
}

const DEFAULT_MAX_SIZE_BYTES = 10 * 1024 * 1024;
const DEFAULT_MAX_FILES = 10;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const makeId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const StagedImageUploader = ({
  mode,
  value,
  onChange,
  label,
  helperText,
  maxFiles,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  accept = 'image/*',
  disabled = false,
}: StagedImageUploaderProps) => {
  const effectiveMaxFiles = mode === 'single' ? 1 : maxFiles ?? DEFAULT_MAX_FILES;
  const [isDragOver, setIsDragOver] = useState(false);
  const [rejections, setRejections] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Revoke object URLs when this component unmounts to avoid memory leaks.
  // We track only URLs we created so we can revoke them once on teardown.
  const createdUrlsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const urls = createdUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const hasUploadActivity = useMemo(
    () => value.some((f) => f.status === 'uploading'),
    [value]
  );

  const handleFiles = useCallback(
    (files: FileList) => {
      const incoming = Array.from(files);
      const nextRejections: string[] = [];

      const accepted: StagedFile[] = [];
      for (const file of incoming) {
        if (!file.type.startsWith('image/')) {
          nextRejections.push(`${file.name}: not an image`);
          continue;
        }
        if (file.size > maxSizeBytes) {
          nextRejections.push(
            `${file.name}: exceeds ${formatFileSize(maxSizeBytes)} limit`
          );
          continue;
        }
        const previewUrl = URL.createObjectURL(file);
        createdUrlsRef.current.add(previewUrl);
        accepted.push({
          id: makeId(),
          file,
          previewUrl,
          status: 'pending',
        });
      }

      let next: StagedFile[];
      if (mode === 'single') {
        // Single mode: replace, keep only first accepted. Revoke old preview.
        const replaced = accepted.slice(0, 1);
        if (replaced.length > 0) {
          value.forEach((f) => {
            URL.revokeObjectURL(f.previewUrl);
            createdUrlsRef.current.delete(f.previewUrl);
          });
          next = replaced;
        } else {
          next = value;
        }
      } else {
        const remainingSlots = Math.max(effectiveMaxFiles - value.length, 0);
        const toAdd = accepted.slice(0, remainingSlots);
        if (accepted.length > remainingSlots) {
          nextRejections.push(
            `Only ${effectiveMaxFiles} files allowed — extra files ignored`
          );
        }
        next = [...value, ...toAdd];
      }

      setRejections(nextRejections);
      if (next !== value) {
        onChange(next);
      }
    },
    [effectiveMaxFiles, maxSizeBytes, mode, onChange, value]
  );

  const handleRemove = useCallback(
    (id: string) => {
      const target = value.find((f) => f.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        createdUrlsRef.current.delete(target.previewUrl);
      }
      onChange(value.filter((f) => f.id !== id));
    },
    [onChange, value]
  );

  const openFilePicker = () => {
    if (!disabled && !hasUploadActivity) {
      fileInputRef.current?.click();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled || hasUploadActivity) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled || hasUploadActivity) return;
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const canAddMore = mode === 'single' ? value.length < 1 : value.length < effectiveMaxFiles;

  return (
    <FormSection
      title={label}
      description={helperText}
      titleAs="h3"
      contentClassName="space-y-3"
    >
      {canAddMore && (
        <div
          role="button"
          tabIndex={0}
          aria-label={`Upload ${label}`}
          aria-disabled={disabled || hasUploadActivity}
          onClick={openFilePicker}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled && !hasUploadActivity) {
              e.preventDefault();
              openFilePicker();
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            disabled || hasUploadActivity
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : isDragOver
                ? 'border-primary-500 bg-primary-50 cursor-pointer'
                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={mode === 'multi'}
            onChange={handleInputChange}
            className="hidden"
            disabled={disabled || hasUploadActivity}
            data-testid={`staged-image-uploader-input-${mode}`}
          />
          <svg
            className="w-10 h-10 text-gray-400 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="text-sm text-gray-600 mt-2">
            <span className="font-medium text-primary-600">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500 mt-1">
            PNG, JPG, GIF up to {formatFileSize(maxSizeBytes)}
            {mode === 'multi' && ` · max ${effectiveMaxFiles} files`}
          </p>
        </div>
      )}

      {rejections.length > 0 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <ul className="text-xs text-yellow-800 space-y-1">
            {rejections.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {value.length > 0 && (
        <div
          className={
            mode === 'single'
              ? 'flex'
              : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3'
          }
        >
          {value.map((staged) => (
            <div
              key={staged.id}
              className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200"
            >
              <img
                src={staged.previewUrl}
                alt={staged.file.name}
                className="w-full h-full object-cover"
              />

              {staged.status === 'uploading' && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {staged.status === 'completed' && (
                <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                  <div className="bg-green-500 rounded-full p-2">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                </div>
              )}

              {staged.status === 'error' && (
                <div className="absolute inset-0 bg-red-500/30 flex items-center justify-center p-2">
                  <div className="bg-red-600 rounded px-2 py-1 text-white text-xs text-center">
                    {staged.errorMessage || 'Upload failed'}
                  </div>
                </div>
              )}

              {staged.status === 'pending' && !disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(staged.id)}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  aria-label={`Remove ${staged.file.name}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}

              <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs px-2 py-1 truncate">
                {staged.file.name}
              </div>
            </div>
          ))}
        </div>
      )}
    </FormSection>
  );
};
